// «💡 Una cosa che forse non sai»: la carta dei consigli in Casa. Un consiglio alla volta
// («Provalo» / «Più tardi» / «Non mi interessa»); dopo averlo provato, la domanda «Com'è andata?»
// con tre risposte e, se vuole, due righe per il caro. Torna subito un contenitore vuoto e lo
// riempie quando sa cosa mostrare: senza consiglio, la Casa è quella di sempre.
import { api } from './api.js';
import { el } from './ui.js';

type Carta =
  | { tipo: 'consiglio'; id: string; titolo: string; testo: string; prova: string }
  | { tipo: 'parere'; id: string; titolo: string; pareri: Array<{ valore: string; testo: string }> }
  | { tipo: 'domanda'; id: string; titolo: string; testo: string; scelte: string[] }
  | null;

export function cartaConsiglio(caro: string): HTMLElement {
  const carta = el('div', { class: 'carta carta-annuncio carta-consiglio', 'data-ruolo': 'consiglio' });
  carta.hidden = true;
  const chiudi = (testo: string): void => {
    carta.replaceChildren(el('p', { class: 'domanda-testo' }, testo));
    window.setTimeout(() => carta.remove(), 4000);
  };

  function consiglio(c: Extract<Carta, { tipo: 'consiglio' }>): void {
    carta.replaceChildren(
      el('p', { class: 'etichetta' }, '💡 Una cosa che forse non sai'),
      el('p', { class: 'consiglio-titolo' }, c.titolo),
      el('p', { class: 'domanda-testo' }, c.testo),
      el('button', {
        class: 'btn-primario',
        click: async () => {
          const r = await api.post<{ rotta: string }>(`/api/consigli/${c.id}/prova`);
          location.hash = r.rotta;
        },
      }, `💡 ${c.prova}`),
      el('div', { class: 'consiglio-altro' },
        el('button', { class: 'btn-quieto', click: async () => { await api.post(`/api/consigli/${c.id}/rimanda`); carta.remove(); } }, 'Più tardi'),
        el('button', { class: 'btn-quieto', click: async () => { await api.post(`/api/consigli/${c.id}/rifiuta`); chiudi('Va bene, non te lo ripropongo.'); } }, 'Non mi interessa'),
      ),
    );
  }

  function parere(c: Extract<Carta, { tipo: 'parere' }>): void {
    carta.replaceChildren(
      el('p', { class: 'etichetta' }, '💡 Com’è andata?'),
      el('p', { class: 'domanda-testo' }, `Hai provato «${c.titolo}». Ti è piaciuto?`),
      el('div', { class: 'consiglio-pareri' }, ...c.pareri.map((p) => el('button', {
        class: 'btn-secondario', type: 'button',
        click: () => commento('💡 Com’è andata?', p.testo, (testo) => api.post(`/api/consigli/${c.id}/parere`, { parere: p.valore, commento: testo })),
      }, p.testo))),
    );
  }

  function domanda(c: Extract<Carta, { tipo: 'domanda' }>): void {
    carta.replaceChildren(
      el('p', { class: 'etichetta' }, '💬 Una domanda del quaderno'),
      el('p', { class: 'domanda-testo' }, c.testo),
      el('div', { class: 'consiglio-pareri' }, ...c.scelte.map((scelta, i) => el('button', {
        class: 'btn-secondario', type: 'button',
        click: () => commento('💬 Una domanda del quaderno', scelta, (testo) => api.post(`/api/consigli/${c.id}/risposta`, { scelta: i, commento: testo })),
      }, scelta))),
      el('button', { class: 'btn-quieto', click: async () => { await api.post(`/api/consigli/${c.id}/rimanda`); carta.remove(); } }, 'Ci penso e te lo dico più tardi'),
    );
  }

  /** Dopo la risposta, due righe facoltative per il caro. */
  function commento(etichetta: string, detto: string, invia: (testo: string) => Promise<unknown>): void {
    const campo = el('textarea', { class: 'consiglio-commento', rows: '3', 'aria-label': `Qualcosa da dire a ${caro}` }) as HTMLTextAreaElement;
    const manda = async (conCommento: boolean): Promise<void> => {
      await invia(conCommento ? campo.value : '');
      chiudi(`Grazie! Lo dico a ${caro}: serve a far diventare il quaderno più comodo per te.`);
    };
    carta.replaceChildren(
      el('p', { class: 'etichetta' }, etichetta),
      el('p', { class: 'domanda-testo' }, `«${detto}». Vuoi aggiungere qualcosa per ${caro}? (se non ti va, va bene lo stesso)`),
      campo,
      el('button', { class: 'btn-primario', click: () => manda(true) }, `Manda a ${caro}`),
      el('button', { class: 'btn-quieto', click: () => manda(false) }, 'No, va bene così'),
    );
    campo.focus();
  }

  void api.get<{ carta: Carta }>('/api/consigli').then(({ carta: c }) => {
    if (!c) { carta.remove(); return; }
    if (c.tipo === 'consiglio') consiglio(c); else if (c.tipo === 'domanda') domanda(c); else parere(c);
    carta.hidden = false;
  }).catch(() => carta.remove());
  return carta;
}
